import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { fetchAuthenticatedBlob } from '../api/client';
import { useAuth } from '../features/auth/AuthProvider';

type AdminAvatarImageProps = {
  readonly avatarUrl: string | null | undefined;
  readonly alt?: string;
  readonly className?: string;
  readonly fallback: ReactNode;
};

/**
 * Renders an admin avatar referenced by `avatar_url`:
 * - absolute http(s) URLs are rendered directly
 * - server-relative paths (e.g. `/admin/admins/{admin_id}/avatar/content`) are
 *   fetched via the authenticated blob endpoint and rendered from a blob URL.
 * Falls back to the supplied node when no URL is present or the fetch fails.
 */
export function AdminAvatarImage({
  avatarUrl,
  alt = '',
  className,
  fallback,
}: AdminAvatarImageProps) {
  const { tokens } = useAuth();
  const absoluteHttp = useMemo(() => {
    const t = avatarUrl?.trim() ?? '';
    if (!t) {
      return null;
    }
    return /^https?:\/\//i.test(t) ? t : null;
  }, [avatarUrl]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (absoluteHttp) {
      return;
    }
    const t = avatarUrl?.trim() ?? '';
    if (!t) {
      setBlobUrl(null);
      return;
    }
    const path = t.startsWith('/') ? t : `/${t}`;
    const token = tokens?.access_token;
    if (!token) {
      setBlobUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchAuthenticatedBlob(path, token).then(
      (blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      },
      () => {
        if (!cancelled) {
          setBlobUrl(null);
        }
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [avatarUrl, tokens?.access_token, absoluteHttp]);

  const src = absoluteHttp ?? blobUrl;
  if (!src) {
    return <>{fallback}</>;
  }
  return <img src={src} alt={alt} className={className} />;
}
